import React, {useEffect, useState} from 'react'
import {getResource} from '../../../data/actions/resource'
import {useDispatch, useSelector} from 'react-redux'
import {Field, FieldsManager} from '../../../data/services/fields'
import LocalStorage from '../../../util/localStorage'
import {checkPerm, getDefaultTableOptions} from '../../../common/util/util-helpers'
import {genericMoneyFormatter} from '../../../common/util/util-vanilla'
import {
    ACCOUNT_TYPE_OTHER_CURRENT_ASSETS,
    DEFAULT_METADATA_SELECT_SEARCH_QUERY,
    READ_PERM
} from '../../../util/util-constants'

import Resources from '../../../data/services/resources'
import {getSecondResource} from '../../../data/actions/secondResource'
import DocumentArrowDownIcon from '@heroicons/react/20/solid/DocumentArrowDownIcon'
import {download} from '../../../data/actions/download'
import {currentDate} from '../../../common/util/util-dates'
import PayDateFilter from "../carrier-pay/carrier-pay-date-filter";
import moment from "moment-timezone";
import ContactInfoDialog from "../../../common/components/modal/contact-info-dialog";
import NoRecordsTable from "../../../common/components/no-records-found/no-records-table";
import ResourceTable from "../../../common/components/resource-table";
import TableFilters from "../../../common/components/resource-table/table-components/table-filters";
import FieldSearch from "../../../common/components/fields/field-text/search";
import TableCard from "../../../common/components/resource-table/table-components/table-card";
import Tooltip from "../../../common/components/tooltip";
import ActiveFilters from "../../../common/components/resource-table/table-components/active-filters";
import CellButton from "../../../common/components/resource-table/table-components/cell-button";
import AdvancesDialog from "../../../common/components/modal/advances-modal";

export default function AdvancesTab({resourceName, translate}) {
    /** Store
     ================================================================= */
    const dispatch = useDispatch()
    const resource = useSelector((state) => state.resource)

    /** Const
     ================================================================= */
    const isLoading = resource.isLoading
    const dataList = resource?.data?.list ?? []
    const dataAccounts = resource?.data?.accounts ?? []

    /** State
     ================================================================= */
    const [isContactAdvancesDialogOpen, setIsContactAdvancesDialogOpen] = useState(false)
    const [selectedContact, setSelectedContact] = useState(null)
    const [isContactInfoDialogOpen, setContactInfoDialogOpen] = useState(null)
    const [filterValue, setFilterValue] = useState('')
    const [query, setQuery] = useState(getQueryFields())
    const [tableOptions, setTableOptions] = useState(getDefaultTableOptions(
        getFields(),
        {},
        'reports-advances',
        translate
    ))

    /** Data Events
     ================================================================= */
    function fetchData() {
        dispatch(getResource({
            user: LocalStorage.get('user'),
            resource: resourceName,
            query: processQuery()
        }))
    }

    function downloadExcel() {
        dispatch(download({
            user: LocalStorage.get('user'),
            resource: resourceName,
            query: Object.assign({
                format: 'EXCEL',
                name: 'reports_advances_' + currentDate() + '.xlsx'
            }, processQuery())
        }))
    }

    /** UI Events
     ================================================================= */
    function handleShowDetailsClick(it) {
        setSelectedContact(it)
        setIsContactAdvancesDialogOpen(true)
    }

    function handleQuerychange(name, value) {
        let queryClone = Object.assign({}, query)

        if (!name && !value) {
            queryClone = Object.values(queryClone).reduce((memo, it) => {
                it.value = ''
                memo[it.name] = it
                return memo
            }, {})
        } else {
            queryClone = FieldsManager.updateField(queryClone, name, value)
        }

        setQuery(queryClone)
    }

    const handleToggleContactInfoDialog = (item = {}) => {
        setSelectedContact(item)
        setContactInfoDialogOpen(!isContactInfoDialogOpen)
    }

    /** Helpers
     ================================================================= */
    function getFields() {
        const accounts = Object.keys(dataAccounts)

        const defaultFields = [
            new Field('LastName', '', [''], false, 'text', {
                order: 1,
                render: (it) => (
                    <CellButton
                        onClick={() => handleToggleContactInfoDialog(it)}>
                        <div>
                            {it.LastName}
                        </div>
                    </CellButton>
                )
            }),
            new Field('FirstName', '', [''], false, 'text', {
                order: 2,
                render: (it) => <CellButton
                    onClick={() => handleToggleContactInfoDialog(it)}>
                    <div>
                        {it.FirstName}
                    </div>
                </CellButton>
            }),
            new Field('Balance', '', [''], false, 'money', {
                render: (it) => <div
                    className="font-bold h-full flex items-center justify-end">{genericMoneyFormatter(it.Balance)}</div>
            })
        ]

        const accountsFields = accounts.reduce((memo, key) => {
            memo.push(new Field(key, '', [''], false, 'money', {
                labelRaw: key,
                render: (it) => <div
                    className="font-bold h-full flex items-center justify-end">{genericMoneyFormatter(it[key])}</div>

            }))
            return memo
        }, [])

        return [...defaultFields, ...accountsFields]
            .reduce((memo, it) => {
                memo[it.name] = it
                return memo
            }, {})
    }

    function getQueryFields() {
        return {
            StartDate: new Field('StartDate', moment().startOf('year').format('YYYY-MM-DD HH:mm:ss'), [], false, 'date', {
                labelType: 'float',
                addContainerClass: 'relative z-40'
            }, {
                isClearable: true,

            }),
            EndDate: new Field('EndDate', moment().endOf('year').format('YYYY-MM-DD HH:mm:ss'), [], false, 'date', {
                labelType: 'float',
                addContainerClass: 'relative z-30'
            }, {
                isClearable: true,
            }),
            Employee: new Field('Employee', '', [''], false, 'select', {labelType: 'float'}, {isClearable: true}),
            Agent: new Field('Agent', '', [''], false, 'select', {labelType: 'float'}, {isClearable: true}),
            Driver: new Field('Driver', '', [''], false, 'select', {labelType: 'float'}, {isClearable: true}),
            AccountIDs: new Field('AccountIDs', '', [''], false, 'select-search', {}, {
                multi: true,
                isClearable: true
            }),
        }
    }

    function processQuery() {
        return Object.values(query).reduce((memo, it) => {
            if (it.name === 'Employee' && it.value === '1') {
                memo['IsEmployee'] = 1
            } else if (it.name === 'Employee' && it.value === '0') {
                memo['NotEmployees'] = 1
            } else if (it.name === 'Agent' && it.value === '1') {
                memo['IsAgent'] = 1
            } else if (it.name === 'Agent' && it.value === '0') {
                memo['NotAgents'] = 1
            } else if (it.name === 'Driver' && it.value === '1') {
                memo['IsDriver'] = 1
            } else if (it.name === 'Driver' && it.value === '0') {
                memo['NotDrivers'] = 1
            } else if (it.name === 'AccountIDs') {
                memo[it.name] = it?.value?.length ? it.value.map(it => it.value) : ''
            } else {
                memo[it.name] = it.value
            }

            return memo
        }, {})
    }

    /** Lifecycle
     ================================================================= */
    useEffect(() => {
        // Update table options
        if (resource.resource === 'accounting/reports/advances') {
            const columns = Object.values(getFields()).reduce((memo, it, i) => {
                let column = {
                    frozen: false,
                    isMerged: false,
                    merged: [],
                    label: it?.metadata?.labelRaw ?? translate('field.' + it.name),
                    minWidth: undefined,
                    name: it.name,
                    order: 1.,
                    show: true,
                    showMobile: false,
                    type: 'money'
                }
                if (['LastName', 'FirstName', 'Balance'].includes(it.name)) {
                    column.frozen = true
                    column.type = 'text'
                    column.showMobile = true
                    column.minWidth = 150
                    if (it.name === 'LastName') {
                        column.order = 1
                    }
                    if (it.name === 'FirstName') {
                        column.order = 2
                    }
                    if (it.name === 'Balance') {
                        column.order = 3
                    }
                } else {
                    column.order = i + 4
                    column.name = resource.data?.accounts?.[it.name]
                    column.label = resource.data?.accounts?.[it.name]
                }

                memo[it.name] = column

                return memo
            }, {})

            let tableOptionsClone = Object.assign({}, tableOptions)
            tableOptionsClone.columns = columns
            tableOptionsClone.behaviour.canAdjustWidth = true
            tableOptionsClone.behaviour.hasMenu = false
            setTableOptions(tableOptionsClone)
        }
    }, [resource])

    useEffect(() => {
        fetchData()
    }, [query])

    /** Render
     ================================================================= */

    const data = dataList.filter(it => {
        return !filterValue ||
            Number(it.FirstName.toLowerCase().search(filterValue.toLowerCase())) > -1
            || Number(it.LastName.toLowerCase().search(filterValue.toLowerCase())) > -1
    })

    return (
        <React.Fragment>
            <div className="min-h-[2.25rem] flex flex-wrap max-w-full items-center">
                <ActiveFilters
                    addClass={'flex-wrap'}
                    filterFields={query}
                    onLabelClick={handleQuerychange}
                    onClearFiltersClick={handleQuerychange}
                    translate={translate}
                />

                <div className="flex ml-auto">
                    <Tooltip content={translate('text.download_excel')}>
                        <button
                            className="btn-icon"
                            onClick={downloadExcel}
                        >
                            <DocumentArrowDownIcon className="w-5 h-5"/>
                        </button>
                    </Tooltip>
                </div>
            </div>

            <TableCard>
                <div className="pl-4 flex items-center gap-2 w-full relative z-30">
                    <FieldSearch
                        addClass={'form-control rounded-md w-full md:ring-1 ring-black/10'}
                        onChange={(_, value) => setFilterValue(value)}
                        value={filterValue}
                    />

                    <TableFilters
                        className="md:px-5 py-5 flex w-full "
                        hideLimit
                        filterFields={query}
                        handleInputChange={handleQuerychange}
                        translate={translate}
                        onRefreshTable={fetchData}
                        isLoading={isLoading}
                        forceDialog
                        addMiscFilterClass={"relative z-0"}
                        customHtml={
                            <PayDateFilter
                                queryFields={query}
                                translate={translate}
                                updateQueryFields={(queryUpdate) => {
                                    setQuery(queryUpdate)
                                }}
                                onQueryChange={handleQuerychange}
                                isDateTypeClearable={false}
                                hasDateType={false}
                                gridCols={'grid-cols-1'}
                            />
                        }
                        selects={{
                            Employee: {
                                '': 'All',
                                '0': 'Hide employees',
                                '1': 'Show employees'
                            },
                            Agent: {
                                '': 'All',
                                '0': 'Hide agents',
                                '1': 'Show agents'
                            },
                            Driver: {
                                '': 'All',
                                '0': 'Hide drivers',
                                '1': 'Show drivers'
                            },
                            AccountIDs: {
                                api: 'api/' + Resources.AccountsQuick,
                                query: {
                                    AccountTypeID: ACCOUNT_TYPE_OTHER_CURRENT_ASSETS,
                                    ...DEFAULT_METADATA_SELECT_SEARCH_QUERY()
                                },
                                searchMap: (item) => ({
                                    label: item.AccountNumber + ' ' + item.AccountName,
                                    value: item.AccountID
                                })
                            },
                        }}
                    />
                </div>

                {!(data.length === 0 && !isLoading) && (
                    <ResourceTable
                        data={data}
                        limit={data.length}
                        fields={getFields()}
                        tableKey="ContactID"
                        maxHeightClass={'max-h-[calc(100vh-20rem)]'}

                        translate={translate}
                        isLoading={isLoading}

                        options={tableOptions}
                        saveTableOptions={setTableOptions}
                        onRowClick={checkPerm(Resources.AccountingAccountReport, READ_PERM) ? handleShowDetailsClick : null}
                        onView={checkPerm(Resources.AccountingAccountReport, READ_PERM) ? handleShowDetailsClick : null}
                        tfoot={
                            Object.assign({
                                    LastName: '',
                                    FirstName: '',
                                    Balance: data.reduce((memo, it) => {
                                        memo = Number(memo) + Number(it.Balance)
                                        return memo
                                    }, 0)
                                }, Object.keys(dataAccounts ?? {}).reduce((memo, colName) => {
                                    memo[colName] = data.reduce((memo, it) => {
                                        memo = Number(memo) + Number(it[colName])
                                        return memo
                                    }, 0)
                                    return memo
                                }, {})
                            )
                        }
                    />
                )}

                <NoRecordsTable
                    show={(data.length === 0) && !resource.isLoading}
                    title={'No matching records found'}
                    onClearFilterClick={() => handleQuerychange()}
                    clearFilterBtnLabel={translate('text.clear_all_filters')}
                    clearFilterText={translate('text.try_without_filters')}
                    filters={query}
                />
            </TableCard>


            <AdvancesDialog
                resourceName={resourceName}
                show={isContactAdvancesDialogOpen}
                downloadExcel={() => {
                    dispatch(download({
                        user: LocalStorage.get('user'),
                        resource: resourceName,
                        query: Object.assign({
                            format: 'EXCEL',
                            name: 'reports_advances_details' + currentDate() + '.xlsx'
                        }, {ContactID: selectedContact?.ContactID})
                    }))
                }}
                onClose={() => setIsContactAdvancesDialogOpen(false)}
                ContactID={selectedContact?.ContactID}
                ContactName={[selectedContact?.FirstName, selectedContact?.LastName].filter(it => !!it).join(' ')}
                AdvanceValue={selectedContact?.Balance ?? 0}
                expenseModalResource={useSelector((state) => state.secondResource)}
                expenseModalOnFetchData={(selectedItem) => {
                    dispatch(getSecondResource({
                        user: LocalStorage.get('user'),
                        query: {
                            ExpenseID: selectedItem?.ExpenseID
                        },
                        resource: Resources.ExpenseExpense
                    }))
                }}
                translate={translate}
            />

            {(isContactInfoDialogOpen &&
                <ContactInfoDialog
                    show={isContactInfoDialogOpen}
                    translate={translate}
                    contactID={selectedContact?.ContactID}
                    handleClose={() => handleToggleContactInfoDialog()}
                />
            )}
        </React.Fragment>
    )
}
